import { getClassName } from "@pairtreefamily/utils";
import { PricingLandingData } from "../../queries/pricingLandingQuery";
import Menu from "../menu";
import { InfoIcon } from "../icons/info";
import { Colors } from "../../types/colors";
import RoundedCheckMark from "../icons/roundedCheckMark";
import {
  PricingFeatureGroup,
  Product,
  ProductFeatures,
  ProductsComparison,
} from "../../types/schema";
import { useState } from "react";

const colorMap = {
  darkblue: "text-darkblue",
  teal: "text-teal",
  green: "text-green",
  lightgreen: "text-lightgreen",
  peach: "text-peach",
  rust: "text-rust",
};

type PricingColor = Extract<
  Colors.Solid,
  "darkblue" | "teal" | "green" | "lightgreen" | "peach" | "rust"
>;

export type ProductsComparisonTableBody = {
  moduleInfo: Pick<PricingLandingData, "moduleProductsComparison">;
};

export function ProductsComparisonTableBody(
  props: ProductsComparisonTableBody,
) {
  const moduleInfo = props.moduleInfo.moduleProductsComparison.moduleInput;

  return (
    <tbody className="table w-full">
      {moduleInfo.featureGroups?.map((group, index) => {
        const featuresFromGroup = moduleInfo.features.filter(
          (item) => item.group?._ref === group?._id,
        );
        return (
          <>
            <tr key={group._id} className="w-full border-b-4 border-[#f2f2f2]">
              <td
                style={{
                  width: `calc(100% / (${
                    (moduleInfo.products?.length ?? 0) + 1
                  })`,
                }}
              >
                <h3
                  className={getClassName(
                    "text-head-7 pb-2 text-onyx",
                    index > 0 && "pt-8",
                  )}
                >
                  {group.name}
                </h3>
              </td>
              {moduleInfo.products?.map((product, index) => (
                <td
                  key={index}
                  style={{
                    width: `calc(100% / (${
                      (moduleInfo.products?.length ?? 0) + 2
                    })`,
                  }}
                  className={getClassName(
                    (index + 1) % 2 == 0 ? "" : "bg-[#f8f8f8]",
                  )}
                ></td>
              ))}
            </tr>
            {featuresFromGroup?.map((feature, index) => (
              <Feature key={index} feature={feature} moduleInfo={moduleInfo} />
            ))}
            <tr></tr>
          </>
        );
      })}
    </tbody>
  );
}

function Feature(props: {
  feature: ProductFeatures;
  moduleInfo: Pick<
    ProductsComparison,
    "heading" | "subHeading" | "unselectedState" | "unavailableState" | "footer"
  > & {
    products: Array<{
      product: Product;
      ctaURL: string;
    }>;
    features: ProductFeatures[];
    featureGroups: PricingFeatureGroup[];
  };
}) {
  const { feature, moduleInfo } = props;
  return (
    <>
      <tr className="w-full border-b-2 border-[#f2f2f2]">
        <td
          style={{
            width: `calc(100% / (${(moduleInfo.products?.length ?? 0) + 2})`,
          }}
        >
          <div className="flex justify-between pr-4">
            <div>
              <p className="py-4 text-body-13 text-onyx">
                <strong>{feature.name}</strong>
              </p>
              {feature.additionalInformation && (
                <p className="text-handwriting-4 mb-4 mt-[-12px] text-darkblue">
                  {feature.additionalInformation}
                </p>
              )}
            </div>
            <div className="flex">
              <Menu
                tooltipBgColor="darkblue"
                openOnHover={true}
                className="!max-w-[250px]"
                id="tooltip-selector"
                elementTrigger={(isOpen) => (
                  <>
                    {isOpen && <InfoIcon type="enabled" fillColor="green" />}
                    {!isOpen && <InfoIcon type="disabled" />}
                  </>
                )}
              >
                <div className="w-full">
                  <p className="text-body-4 p-4 text-white">
                    {feature.tooltip}
                  </p>
                </div>
              </Menu>
            </div>
          </div>
        </td>
        {moduleInfo.products?.map((product, index) => {
          const featureFound = product.product?.features?.find(
            (productFeatures) => productFeatures.feature?._ref === feature._id,
          );
          if (featureFound) {
            return (
              <>
                <td
                  key={index}
                  className={getClassName(
                    "text-center",
                    (index + 1) % 2 == 0 ? "" : "bg-[#f8f8f8]",
                  )}
                  style={{
                    width: `calc(100% / (${
                      (moduleInfo.products?.length ?? 0) + 2
                    })`,
                  }}
                >
                  {featureFound.textLine1 && (
                    <span
                      className={getClassName(
                        "text-overline-2",
                        colorMap[product.product?.color as PricingColor],
                      )}
                    >
                      {featureFound.textLine1}
                    </span>
                  )}
                  {!featureFound.textLine1 && (
                    <div
                      className={getClassName(
                        "flex justify-center",
                        colorMap[product.product?.color as PricingColor],
                      )}
                    >
                      <span>
                        <RoundedCheckMark />
                      </span>
                    </div>
                  )}
                </td>
              </>
            );
          }
          return (
            <td
              key={index}
              className={getClassName(
                "text-center",
                (index + 1) % 2 == 0 ? "" : "bg-[#f8f8f8]",
              )}
              style={{
                width: `calc(100% / (${
                  (moduleInfo.products?.length ?? 0) + 2
                })`,
              }}
            >
              <p className="text-center text-heading-30 text-lightgray">–</p>
            </td>
          );
        })}
      </tr>
    </>
  );
}
